// scene-core/voxel-grid.ts — reader for the sparse voxel octree written by
// PlayCanvas splat-transform (and LichtFeld Studio), format v1.1: a
// `.voxel.json` metadata file plus a `.voxel.bin` holding
//
//   nodes    Uint32[nodeCount]       Laine-Karras octree, breadth-first
//   leafData Uint32[leafDataCount]   (lo, hi) occupancy pairs, one per mixed leaf
//
// The leaves are blocks of 4x4x4 voxels. A node is one of:
//   0xFF000000          solid: every voxel under it is occupied (at any level,
//                       a solid node at height k covers 2^k blocks per axis)
//   top byte 0          mixed leaf: low 24 bits index leafData pairs; voxel
//                       (lx, ly, lz) is bit lx + 4·ly + 16·lz of (lo | hi<<32)
//   otherwise           interior: top byte = child mask (bit0 = +x, bit1 = +y,
//                       bit2 = +z half), low 24 bits = index of the first
//                       child; children sit contiguously in octant order, so
//                       child o is at first + popcount(mask & ((1<<o) - 1)).
// Absent children are empty. The root covers 2^treeDepth blocks per axis and
// its corner is gridBounds.min, so voxel (ix, iy, iz) spans
// min + [i, i+1)·voxelResolution on each axis.
//
// Coordinates here are the VOXEL frame (whatever frame the splat was in when
// it was voxelized). Mapping to the walk world lives in voxel-walk.ts.
//
// Pure TypeScript, no playcanvas / DOM, shared by the editor and viewer2 like
// walk-geometry.ts.

export interface VoxelMeta {
    version: string;
    gridBounds: { min: number[]; max: number[] };
    sceneBounds?: { min: number[]; max: number[] };
    voxelResolution: number;
    leafSize: number;
    treeDepth: number;
    nodeCount: number;
    leafDataCount: number;
    numInteriorNodes?: number;
    numMixedLeaves?: number;
}

const SOLID = 0xFF000000;

/** Popcount of the low 8 bits (child masks only use 8). */
const popcount8 = (v: number): number => {
    v = v - ((v >> 1) & 0x55);
    v = (v & 0x33) + ((v >> 2) & 0x33);
    return (v + (v >> 4)) & 0x0F;
};

/** Axis-aligned box in voxel indices, max exclusive. */
export interface VoxelBox {
    min: [number, number, number];
    max: [number, number, number];
}

export class VoxelGrid {
    readonly meta: VoxelMeta;
    readonly nodes: Uint32Array;
    readonly leafData: Uint32Array;
    /** Voxel edge length (metres). */
    readonly res: number;
    /** Voxel-frame position of voxel (0,0,0)'s min corner. */
    readonly origin: [number, number, number];
    /** Voxels per axis covered by the root (4 · 2^treeDepth). */
    readonly size: number;

    constructor(meta: VoxelMeta, nodes: Uint32Array, leafData: Uint32Array) {
        if (meta.leafSize !== undefined && meta.leafSize !== 4) {
            throw new Error(`voxel grid: leafSize ${meta.leafSize} is not supported (expected 4)`);
        }
        this.meta = meta;
        this.nodes = nodes;
        this.leafData = leafData;
        this.res = meta.voxelResolution;
        this.origin = [meta.gridBounds.min[0], meta.gridBounds.min[1], meta.gridBounds.min[2]];
        this.size = 4 << meta.treeDepth;
    }

    /** Build from the parsed .voxel.json and the raw .voxel.bin bytes. */
    static fromBuffers(meta: VoxelMeta, bin: ArrayBuffer): VoxelGrid {
        const words = bin.byteLength >> 2;
        if (words < meta.nodeCount + meta.leafDataCount) {
            throw new Error(`voxel grid: .bin holds ${words} words, metadata needs ${meta.nodeCount + meta.leafDataCount}`);
        }
        const nodes = new Uint32Array(bin, 0, meta.nodeCount);
        const leafData = new Uint32Array(bin, meta.nodeCount * 4, meta.leafDataCount);
        return new VoxelGrid(meta, nodes, leafData);
    }

    /** Is voxel (ix, iy, iz) occupied? Out-of-grid voxels are empty. */
    isSolid(ix: number, iy: number, iz: number): boolean {
        const size = this.size;
        if (ix < 0 || iy < 0 || iz < 0 || ix >= size || iy >= size || iz >= size) return false;
        if (this.nodes.length === 0) return false;
        const bx = ix >> 2, by = iy >> 2, bz = iz >> 2;
        const nodes = this.nodes;
        let node = 0;
        let half = (size >> 2) >> 1;   // blocks per child at the root
        for (;;) {
            const v = nodes[node];
            if (v === SOLID) return true;
            const mask = v >>> 24;
            if (mask === 0) {
                // mixed leaf
                const li = (v & 0xFFFFFF) * 2;
                const bit = (ix & 3) + ((iy & 3) << 2) + ((iz & 3) << 4);
                return bit < 32
                    ? (this.leafData[li] & (1 << bit)) !== 0
                    : (this.leafData[li + 1] & (1 << (bit - 32))) !== 0;
            }
            if (half === 0) return false;   // malformed: interior below block level
            const o = ((bx & half) ? 1 : 0) | ((by & half) ? 2 : 0) | ((bz & half) ? 4 : 0);
            if ((mask & (1 << o)) === 0) return false;
            node = (v & 0xFFFFFF) + popcount8(mask & ((1 << o) - 1));
            half >>= 1;
        }
    }

    /** Voxel index containing voxel-frame point p (may be outside the grid). */
    indexOf(x: number, y: number, z: number, out: [number, number, number] = [0, 0, 0]): [number, number, number] {
        out[0] = Math.floor((x - this.origin[0]) / this.res);
        out[1] = Math.floor((y - this.origin[1]) / this.res);
        out[2] = Math.floor((z - this.origin[2]) / this.res);
        return out;
    }

    /** Is the voxel-frame point p inside an occupied voxel? */
    isSolidAt(x: number, y: number, z: number): boolean {
        return this.isSolid(
            Math.floor((x - this.origin[0]) / this.res),
            Math.floor((y - this.origin[1]) / this.res),
            Math.floor((z - this.origin[2]) / this.res)
        );
    }

    /**
     * Visit every occupied run of the octree that overlaps `box` (voxel
     * indices, max exclusive): `solid(x0,y0,z0, n)` for an all-solid cube of
     * n voxels per side at (x0,y0,z0), `mixed(x0,y0,z0, lo, hi)` for a 4³
     * block with its occupancy bits. Runs are NOT clipped to the box.
     */
    visit(
        box: VoxelBox,
        solid: (x0: number, y0: number, z0: number, n: number) => void,
        mixed: (x0: number, y0: number, z0: number, lo: number, hi: number) => void
    ): void {
        if (this.nodes.length === 0) return;
        const nodes = this.nodes;
        const leaf = this.leafData;
        const stack: number[] = [0, 0, 0, 0, this.size];   // node, x, y, z, size (voxels)
        while (stack.length) {
            const n = stack.pop()!;
            const z = stack.pop()!;
            const y = stack.pop()!;
            const x = stack.pop()!;
            const node = stack.pop()!;
            if (x >= box.max[0] || y >= box.max[1] || z >= box.max[2] ||
                x + n <= box.min[0] || y + n <= box.min[1] || z + n <= box.min[2]) continue;
            const v = nodes[node];
            if (v === SOLID) { solid(x, y, z, n); continue; }
            const mask = v >>> 24;
            if (mask === 0) {
                const li = (v & 0xFFFFFF) * 2;
                mixed(x, y, z, leaf[li], leaf[li + 1]);
                continue;
            }
            const h = n >> 1;
            if (h < 4) continue;   // malformed
            let child = v & 0xFFFFFF;
            for (let o = 0; o < 8; o++) {
                if ((mask & (1 << o)) === 0) continue;
                stack.push(child++, x + ((o & 1) ? h : 0), y + ((o & 2) ? h : 0), z + ((o & 4) ? h : 0), h);
            }
        }
    }

    /**
     * Dense occupancy of `box` as one bit per voxel, x fastest:
     * bit (x + y·sx + z·sx·sy) of the returned words, relative to box.min.
     * Memory is sx·sy·sz/8 bytes — callers keep the box modest.
     */
    denseBits(box: VoxelBox): { bits: Uint32Array; sx: number; sy: number; sz: number } {
        const [x0, y0, z0] = box.min;
        const sx = box.max[0] - x0, sy = box.max[1] - y0, sz = box.max[2] - z0;
        const bits = new Uint32Array(Math.ceil((sx * sy * sz) / 32));
        const set = (x: number, y: number, z: number) => {
            const i = x + sx * (y + sy * z);
            bits[i >>> 5] |= 1 << (i & 31);
        };
        this.visit(box, (bx, by, bz, n) => {
            const ax = Math.max(bx, x0), bxe = Math.min(bx + n, box.max[0]);
            const ay = Math.max(by, y0), bye = Math.min(by + n, box.max[1]);
            const az = Math.max(bz, z0), bze = Math.min(bz + n, box.max[2]);
            for (let z = az; z < bze; z++) {
                for (let y = ay; y < bye; y++) {
                    for (let x = ax; x < bxe; x++) set(x - x0, y - y0, z - z0);
                }
            }
        }, (bx, by, bz, lo, hi) => {
            for (let b = 0; b < 64; b++) {
                if (b < 32 ? (lo & (1 << b)) === 0 : (hi & (1 << (b - 32))) === 0) continue;
                const x = bx + (b & 3), y = by + ((b >> 2) & 3), z = bz + (b >> 4);
                if (x < x0 || y < y0 || z < z0 || x >= box.max[0] || y >= box.max[1] || z >= box.max[2]) continue;
                set(x - x0, y - y0, z - z0);
            }
        });
        return { bits, sx, sy, sz };
    }

    /**
     * Surface voxels inside `box`: occupied voxels with at least one empty
     * face neighbour (neighbours outside the box count as empty). Returns
     * voxel indices as [x, y, z, faces, ...] where `faces` is a 6-bit mask of
     * the exposed sides (bit0 -x, bit1 +x, bit2 -y, bit3 +y, bit4 -z, bit5 +z).
     * Stops after `limit` voxels; `truncated` says whether it did.
     */
    surfaceVoxels(box: VoxelBox, limit = Infinity): { voxels: Int32Array; count: number; truncated: boolean } {
        const { bits, sx, sy, sz } = this.denseBits(box);
        const get = (x: number, y: number, z: number) => {
            if (x < 0 || y < 0 || z < 0 || x >= sx || y >= sy || z >= sz) return false;
            const i = x + sx * (y + sy * z);
            return (bits[i >>> 5] & (1 << (i & 31))) !== 0;
        };
        let out = new Int32Array(4096 * 4);
        let count = 0;
        let truncated = false;
        const total = sx * sy * sz;
        for (let w = 0; w < bits.length && !truncated; w++) {
            const word = bits[w];
            if (word === 0) continue;
            for (let b = 0; b < 32; b++) {
                if ((word & (1 << b)) === 0) continue;
                const i = w * 32 + b;
                if (i >= total) break;
                const x = i % sx, yz = (i - x) / sx, y = yz % sy, z = (yz - y) / sy;
                const faces =
                    (get(x - 1, y, z) ? 0 : 1) | (get(x + 1, y, z) ? 0 : 2) |
                    (get(x, y - 1, z) ? 0 : 4) | (get(x, y + 1, z) ? 0 : 8) |
                    (get(x, y, z - 1) ? 0 : 16) | (get(x, y, z + 1) ? 0 : 32);
                if (faces === 0) continue;
                if (count >= limit) { truncated = true; break; }
                if ((count + 1) * 4 > out.length) {
                    const grown = new Int32Array(out.length * 2);
                    grown.set(out);
                    out = grown;
                }
                const o = count * 4;
                out[o] = x + box.min[0]; out[o + 1] = y + box.min[1]; out[o + 2] = z + box.min[2]; out[o + 3] = faces;
                count++;
            }
        }
        return { voxels: out.subarray(0, count * 4), count, truncated };
    }
}

/**
 * Fetch `<name>.voxel.json` and its sibling `<name>.voxel.bin`. `url` may be
 * either file; the other is derived by swapping the extension.
 */
/** The raw .voxel.json and .voxel.bin behind a voxel URL (either file's URL works). */
export const fetchVoxelFiles = async (url: string, fetchImpl: typeof fetch = fetch): Promise<{ json: Uint8Array; bin: ArrayBuffer }> => {
    const base = url.replace(/\.voxel\.(json|bin)(\?.*)?$/, '');
    const query = url.match(/\?.*$/)?.[0] ?? '';
    const [metaRes, binRes] = await Promise.all([
        fetchImpl(`${base}.voxel.json${query}`),
        fetchImpl(`${base}.voxel.bin${query}`)
    ]);
    if (!metaRes.ok) throw new Error(`voxel metadata fetch failed: ${metaRes.status} (${base}.voxel.json)`);
    if (!binRes.ok) throw new Error(`voxel data fetch failed: ${binRes.status} (${base}.voxel.bin)`);
    return { json: new Uint8Array(await metaRes.arrayBuffer()), bin: await binRes.arrayBuffer() };
};

export const loadVoxelGrid = async (url: string, fetchImpl: typeof fetch = fetch): Promise<VoxelGrid> => {
    const { json, bin } = await fetchVoxelFiles(url, fetchImpl);
    return VoxelGrid.fromBuffers(JSON.parse(new TextDecoder().decode(json)) as VoxelMeta, bin);
};
